/**
 * Preset set v2.0 (decision D-020).
 *
 * Every preset carries a written retention rationale. This is not documentation
 * for its own sake: the first version of this table shipped a Receipt preset at
 * 30 days, which would have destroyed a self-employed user's HMRC records. The
 * rationale field exists so that never happens silently again.
 */

/** Built-in ids. Users can add their own, so the effective type of a stored
 *  preset id is a plain string — see the runtime registry in the client. */
export type PresetId = 'just-for-now' | 'parking' | 'wifi' | 'notes' | 'keep';

/** null means no expiry at all. */
export type Lifetime = { kind: 'duration'; ms: number } | { kind: 'end-of-day' } | null;

export interface Preset {
  readonly id: PresetId;
  readonly label: string;
  readonly lifetime: Lifetime;
  /** Shown to the user the moment they choose it — they must know what they agreed to. */
  readonly consequence: string;
  readonly rationale: string;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export const PRESETS: readonly Preset[] = [
  {
    id: 'just-for-now',
    label: 'Just for now',
    lifetime: { kind: 'duration', ms: 24 * HOUR },
    consequence: 'kept for 24 hours',
    rationale:
      'The dominant real category: serial numbers, error screens, an address, a shelf in a shop. Also the default when nothing is chosen.',
  },
  {
    id: 'parking',
    label: 'Parking',
    lifetime: { kind: 'end-of-day' },
    consequence: 'kept until 23:59 tonight',
    rationale: 'Worthless once the trip is over. Unanimous across the discovery panel.',
  },
  {
    id: 'wifi',
    label: 'Wifi & codes',
    lifetime: { kind: 'duration', ms: 7 * DAY },
    consequence: 'kept for 7 days',
    rationale: 'One stay, one trip. 24 hours was too short for a holiday let.',
  },
  {
    id: 'notes',
    label: 'Notes & boards',
    lifetime: { kind: 'duration', ms: 30 * DAY },
    consequence: 'kept for 30 days',
    rationale:
      'Whiteboards, noticeboards, school letters, part numbers. Long enough to write it up properly.',
  },
  {
    id: 'keep',
    label: 'Keep',
    lifetime: null,
    consequence: 'no expiry — yours until you say otherwise',
    rationale:
      'Receipts, tax records, dispute evidence, ID. Anything with money or liability attached. A UK self-employed user needs receipts for roughly six years; consumer warranties run two.',
  },
];

export const DEFAULT_PRESET: PresetId = 'just-for-now';

export function getPreset(id: PresetId): Preset {
  const p = PRESETS.find((x) => x.id === id);
  if (!p) throw new Error(`Unknown preset: ${id}`);
  return p;
}

/**
 * @param nowMs        current instant
 * @param utcOffsetMin the device's offset from UTC in minutes, so "end of day"
 *                     means the user's midnight rather than the server's.
 * @returns epoch ms, or null for a lifetime that never expires.
 */
export function expiryFromLifetime(lifetime: Lifetime, nowMs: number, utcOffsetMin = 0): number | null {
  if (lifetime === null) return null;
  if (lifetime.kind === 'duration') return nowMs + lifetime.ms;

  // End of the user's local day, expressed back in UTC.
  const offsetMs = utcOffsetMin * 60_000;
  const local = nowMs + offsetMs;
  const startOfLocalDay = Math.floor(local / DAY) * DAY;
  const endOfLocalDay = startOfLocalDay + DAY - 1000; // 23:59:59 local
  return endOfLocalDay - offsetMs;
}

/** Convenience for the built-in presets. */
export function computeExpiry(id: PresetId, nowMs: number, utcOffsetMin = 0): number | null {
  return expiryFromLifetime(getPreset(id).lifetime, nowMs, utcOffsetMin);
}

/** Human wording for a lifetime, shown the moment a type is chosen. */
export function describeLifetime(lifetime: Lifetime): string {
  if (lifetime === null) return 'no expiry — yours until you say otherwise';
  if (lifetime.kind === 'end-of-day') return 'kept until 23:59 tonight';
  const hours = lifetime.ms / HOUR;
  if (hours < 24) return `kept for ${round(hours)} ${plural(round(hours), 'hour')}`;
  const days = hours / 24;
  return `kept for ${round(days)} ${plural(round(days), 'day')}`;
}

const round = (n: number): number => Math.round(n * 10) / 10;
const plural = (n: number, word: string): string => (n === 1 ? word : `${word}s`);
